import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getMovieById, getCategoryName, movies } from '@/mocks/movies';

function getRelatedMovies(category: string, currentId: number) {
  return movies.filter((m) => m.category === category && m.id !== currentId).slice(0, 6);
}

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movieId = id ? parseInt(id, 10) : 0;
  const movie = getMovieById(movieId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    if (movie) {
      document.title = `${movie.title} - 7777影院在线观看 | 高清${movie.tags.join('')}片`;
    }
  }, [movie]);

  if (!movie) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">内容未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">您要查看的影视内容不存在或已下架</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedMovies = getRelatedMovies(movie.category, movie.id);
  const categoryName = getCategoryName(movie.category);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${movie.title} - 7777影院在线观看`,
    description: movie.description,
    datePublished: `${movie.year}-01-01`,
    dateModified: movie.updateDate,
    author: {
      '@type': 'Organization',
      name: '7777影院',
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName, href: `/category/${movie.category}` },
    { label: movie.title },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <article className="flex flex-col lg:flex-row gap-8 mb-12">
            <div className="flex-shrink-0 w-full sm:w-[300px] lg:w-[350px]">
              <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
                <img
                  src={movie.posterUrl}
                  alt={movie.title}
                  title={`${movie.title} - 7777影院高清在线观看`}
                  className="w-full h-full object-cover object-top"
                />
                {movie.rating && (
                  <div className="absolute top-3 right-3 bg-accent-500 text-background-50 text-sm font-bold px-3 py-1 rounded-lg">
                    {movie.rating} 分
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {movie.title}
              </h1>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full font-medium"
                  >
                    {tag}
                  </span>
                ))}
                {movie.episodes && (
                  <span className="text-xs bg-accent-100 text-accent-700 px-2.5 py-1 rounded-full font-medium">
                    {movie.episodes}
                  </span>
                )}
              </div>

              <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                {movie.description}
              </p>

              <div className="grid grid-cols-2 gap-y-3 gap-x-6 mb-6 bg-background-100 rounded-lg p-5">
                <div>
                  <span className="text-xs text-foreground-500">导演</span>
                  <p className="text-sm font-medium text-foreground-900 mt-0.5">{movie.director}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">主演</span>
                  <p className="text-sm font-medium text-foreground-900 mt-0.5">{movie.actors.join(' / ')}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">地区</span>
                  <p className="text-sm font-medium text-foreground-900 mt-0.5">{movie.region}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">年份</span>
                  <p className="text-sm font-medium text-foreground-900 mt-0.5">{movie.year}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">时长</span>
                  <p className="text-sm font-medium text-foreground-900 mt-0.5">{movie.duration}</p>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">更新日期</span>
                  <p className="text-sm font-medium text-foreground-900 mt-0.5">{movie.updateDate}</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-play-circle-line text-lg"></i>
                  立即播放
                </button>
                <button className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer border border-background-200">
                  <i className="ri-heart-line text-lg"></i>
                  收藏
                </button>
                <button className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer border border-background-200">
                  <i className="ri-share-forward-line text-lg"></i>
                  分享
                </button>
              </div>
            </div>
          </article>

          <section className="mb-10">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">剧情简介</h2>
            <div className="bg-background-100 rounded-lg p-6">
              <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                {movie.longDescription}
              </p>
            </div>
          </section>

          {relatedMovies.length > 0 && (
            <section className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-heading font-bold text-foreground-900">
                  相关推荐
                </h2>
                <Link
                  to={`/category/${movie.category}`}
                  className="text-sm text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer"
                >
                  更多{categoryName} <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                {relatedMovies.map((m) => (
                  <Link
                    key={m.id}
                    to={`/detail/${m.id}`}
                    className="group block cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200 mb-2">
                      <img
                        src={m.posterUrl}
                        alt={m.title}
                        title={`${m.title} - 7777影院高清在线观看`}
                        className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                      />
                      {m.rating && (
                        <div className="absolute top-1.5 right-1.5 bg-accent-500 text-background-50 text-[10px] font-bold px-1.5 py-0.5 rounded">
                          {m.rating}
                        </div>
                      )}
                    </div>
                    <h3 className="text-xs font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {m.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
}